import { useContext, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import settingService from "api/settingsService";
import classNames from "classnames";
import Input from "components/Input";
import Avatar from "components/Settings/PersonalTab/Avatar";
import { Claims } from "dto/User/claims";
import { useAuth } from "react-oidc-context";
import { getUserClaim } from "utils/getUserClaim";
import { getUserIdentityVerified } from "utils/getUserIdentityVerified";
import { ENotificationType, notification } from "utils/notification";
import ChangePassword from "../ChangePassword";
import userService from "api/userService";
import Button from "components/Button";
import ChangePhone from "../ChangePhone";
import ConfirmModal from "components/ConfirmModal";
import HollowButton from "components/HollowButton";
import { useNavigate } from "react-router-dom";
import ModalsManagerContext from "contexts/ModalsManager";
import ReactDOM from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import * as openpgp from "openpgp";
import { getUserId } from "utils/getUserId";
import { getKeys } from "utils/getKeys";
import { clearAllKeys } from "utils/clearAllKeys";
import CopyIcon from "assets/icons/copy.svg?react";
import QrCodeIcon from "assets/icons/qr2.svg?react";
import CloseIcon from "assets/icons/close.svg?react";
import { useTranslation } from "react-i18next";

export enum PersonalTabMode {
  Normal,
  ChangePassword,
  Address,
  ChangePhone,
}

const PersonalTab: FC<Props> = ({ showTabBar }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const userId = getUserId(auth.user!);
  const { modals, setModals } = useContext(ModalsManagerContext);
  const { data: phoneNumberData, refetch: phonerefetch } =
    settingService.useGetPhoneNumber();
  const { mutate: removeAccount } = userService.useRemoveAccount();
  const inputFile = useRef<HTMLInputElement | null>(null);

  const [mode, setMode] = useState<PersonalTabMode>(PersonalTabMode.Normal);
  const [isVerified, setIsVerified] = useState<boolean>(false);
  const [avatarRefetchTrigger] = useState<number>(0);
  const [profileData, setProfileData] = useState({
    pesel: "",
    email: "",
  });

  const [showCloseAccount, setShowCloseAccount] = useState<boolean>(false);

  const changeMode = (mode: PersonalTabMode) => {
    setMode(mode);
    showTabBar(mode === PersonalTabMode.Normal);
  };

  const onInnerViewCanceled = () => {
    changeMode(PersonalTabMode.Normal);
  };

  const onInnerViewSaved = () => {
    phonerefetch();
    changeMode(PersonalTabMode.Normal);
  };

  const handleBrowseAvatar = () => {
    if (inputFile && inputFile.current) {
      inputFile.current.click();
    }
  };

  useEffect(() => {
    if (auth.user) {
      setIsVerified(getUserIdentityVerified(auth.user!));
      setProfileData({
        ...profileData,
        pesel: getUserClaim(auth.user, Claims.Eidas_PersonIdentifier),
        email: getUserClaim(auth.user, Claims.Email),
      });
    }
  }, [auth.user]);



  const [email, setEmail] = useState(profileData?.email);
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [key, setKey] = useState<string>(null);
  const [showQrCode, setShowQrCode] = useState<boolean>(false);

  const loadKey = async () => {
    const recovKey = localStorage.getItem(`${userId}.recov`);

    if (recovKey?.length) {
      const userKeys = await getKeys(userId);

      const intermediateKey = await openpgp.key.readArmored(
        atob(userKeys!.intermidatePrivateKey!.data),
      );

      const options: openpgp.DecryptOptions = {
        message: await openpgp.message.readArmored(atob(recovKey)),
        privateKeys: intermediateKey.keys,
      };

      openpgp.decrypt(options).then((r) => {
        setKey(r.data);
      });
    }
  };

  useEffect(() => {
    loadKey();
  }, []);

  const qrCodeRef = useRef(null);

  useEffect(() => {
    if (qrCodeRef?.current) {
      const qrData =
        import.meta.env.VITE_APP_SITE + `/recoverqr/${userId}#${key}`;

      ReactDOM.render(
        <QRCodeCanvas value={qrData} size={200} />,
        qrCodeRef?.current,
      );
    }
  }, [qrCodeRef, key, showQrCode]);

  useEffect(() => {
    console.log(phoneNumberData);
    if (!phoneNumberData?.data?.Phone) return;
    console.log("phone", phoneNumberData.data.Phone);
    setPhone(phoneNumberData?.data?.Phone);
  }, [phoneNumberData]);

  const [edit] = useState({
    email: false,
    password: false,
    phone: false,
  });

  useEffect(() => {
    setEmail(profileData?.email);
  }, [profileData]);

  const navigate = useNavigate();

  const isLegalPerson =
    getUserClaim(auth.user, Claims.UserTypeClaim) === "legalperson";

  const handleCloseAccount = () => {
    removeAccount(
      {},
      {
        onSuccess(data, variables, context) {
          // Materiał kluczowy czyścimy PRZED przekierowaniem do wylogowania — po
          // signoutRedirect() nawigacja już trwa, a userId pochodzi z auth.user, które
          // wylogowanie zaraz wyczyści (patrz komentarz w utils/clearAllKeys).
          clearAllKeys(userId);
          auth.signoutRedirect();
        },
        onError(error, variables, context) {
          notification({
            title: t("settings.personal.closeAccountFailed"),
            text: t("settings.personal.tryAgain"),
            type: ENotificationType.ERROR,
          });
        },
      },
    );
  };

  const handleReverify = () => {
    setModals({ ...modals, settings: false });
    navigate("/identify");
  };

  return (
    <div className={styles.wrapper}>
      {mode === PersonalTabMode.Normal && (
        <>
          <div className={styles.primary}>
            <div className={styles.avatar}>
              <Avatar
                onClick={handleBrowseAvatar}
                className={styles.avatar}
                name={auth.user?.profile.name ?? ""}
                refetchTrigger={avatarRefetchTrigger}
              />
              <div className={styles.data}>
                <div className={styles.bold}>
                  {t("settings.personal.greeting", {
                    name: auth.user?.profile.name,
                  })}{" "}
                </div>
                <div className={styles.hint}>
                  {t("settings.personal.dataHint")}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.twoColumn}>
            <div className={styles.leftColumn}>
              <div className={styles.columnTitle}>
                {t("settings.personal.privateProfile")}
                <span className={classNames(styles.statusBadge, isVerified ? styles.statusActive : styles.statusInactive)}>
                  {isVerified ? t("common.active") : t("common.inactive")}
                </span>
              </div>
              <div className={styles.personPinfo}>
                <div className={styles.Pinfo}>
                  <div className={styles.s}>
                    <div className={styles.left}>
                      <div className={styles.nazwa}>
                        {auth.user?.profile.name}
                      </div>
                      <div className={styles.pesel}>
                        {t("forms.pesel")}: {profileData?.pesel}
                      </div>
                      {
                        <div
                          className={classNames(
                            styles.verification,
                            isVerified ? styles.verified : "",
                          )}
                        >
                          {isVerified
                            ? t("settings.personal.dataVerified")
                            : t("settings.personal.dataNotVerified")}
                        </div>
                      }
                    </div>
                    <div className={styles.right}>
                      {!isLegalPerson && (
                        <div className={styles.update}>
                          <HollowButton
                            text={t("settings.personal.updateData")}
                            onClick={handleReverify}
                          ></HollowButton>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.Pinfo}>
                  <Input
                    className={styles.PInput}
                    value={email}
                    disabled={!edit.email}
                    label={t("auth.email")}
                    focus={!!edit.email}
                    onChange={(e) => {
                      setEmail(e);
                    }}
                  />
                  <div style={{ marginLeft: "16px" }} className={styles.e}>
                  </div>
                </div>
                <div className={styles.Pinfo}>
                  <Input
                    className={styles.PInput}
                    value={!edit.password ? "randomowhaseło" : password}
                    disabled={!edit.password}
                    label={t("auth.password")}
                    type="password"
                    onRightText={t("common.edit")}
                    onRightClick={() => {
                      setMode(PersonalTabMode.ChangePassword);
                    }}
                    onChange={(e) => {
                      setPassword(e);
                    }}
                  />
                  <div style={{ marginLeft: "16px" }} className={styles.e}>
                  </div>
                </div>
                {!!key?.length && (
                  <div className={styles.Pinfo}>
                    <Input
                      className={styles.PInput}
                      value={key}
                      disabled={!edit.password}
                      label={t("settings.personal.recoveryKey")}
                      type="password"
                      allowToShowPassword={true}
                      RightIcon={
                        <div className={styles.icons}>
                          <div onClick={() => {}}>
                            <CopyIcon></CopyIcon>
                          </div>
                          <div
                            onClick={() => {
                              setShowQrCode(true);
                            }}
                          >
                            <QrCodeIcon></QrCodeIcon>
                          </div>
                        </div>
                      }
                      onChange={(e) => {}}
                    />
                    <div style={{ marginLeft: "16px" }} className={styles.e}>
                    </div>
                  </div>
                )}
                <div className={styles.Pinfo}>
                  <Input
                    className={styles.PInput}
                    value={phone ?? t("common.none")}
                    disabled={!edit.phone}
                    label={t("forms.phoneNumber")}
                    onChange={() => {}}
                    onRightText={t("common.edit")}
                    onRightClick={() => {
                      setMode(PersonalTabMode.ChangePhone);
                    }}
                  />
                  <div style={{ marginLeft: "16px" }} className={styles.e}>
                  </div>
                </div>
              </div>
            </div>

          </div>
          <hr className={styles.divider} />
          <div className={styles.group}>
            <div className={styles.title}>
              {t("settings.personal.closeAccount")}
            </div>
            <div className={styles.hint}>
              {t("settings.personal.closeAccountHint")}
            </div>

            <div className={styles.buttonWrapper}>
              <div
                className={styles.btn}
                onClick={() => {
                  setShowCloseAccount(true);
                }}
              >
                {t("common.close")}
              </div>
              <Button
                className={styles.errorButton}
                color="error"
                text={t("settings.personal.closeAndDownload")}
                onClick={() => {
                  window.open(
                    `${import.meta.env.VITE_APP_SITE}/export`,
                    "_blank",
                  );
                }}
              />
            </div>
          </div>
        </>
      )}
      {mode == PersonalTabMode.ChangePassword && (
        <ChangePassword
          onCanceled={onInnerViewCanceled}
          onSaved={onInnerViewSaved}
        ></ChangePassword>
      )}
      {mode == PersonalTabMode.ChangePhone && (
        <ChangePhone
          phone={phone}
          onCanceled={onInnerViewCanceled}
          onSaved={onInnerViewSaved}
        ></ChangePhone>
      )}

      {showCloseAccount && (
        <ConfirmModal
          title={t("settings.personal.closeProcedure")}
          text={t("settings.personal.closeProcedureText")}
          acceptLabel={t("settings.personal.closeAccountLabel")}
          cancelLabel={t("common.cancel")}
          showDontShowNextTime={false}
          onAccept={() => {
            handleCloseAccount();
          }}
          onCancel={() => setShowCloseAccount(false)}
        />
      )}

      {showQrCode && (
        <>
          <div className={styles.modalBg}></div>
          <div className={styles.modalWrapper}>
            <div className={styles.header}>
              {t("settings.personal.recoveryKey")}
              <CloseIcon
                onClick={() => {
                  setShowQrCode(false);
                }}
                className={styles.closeIcon}
              />
            </div>
            <div className={styles.qrCodeWrapper}>
              <div className={styles.qrcode} ref={qrCodeRef}></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PersonalTab;
